import { useMemo } from 'react';
import { Cluster } from '../../clustering/clustering.types';
import { Dimension, VectorScale } from '../types/web-gl.type';

export const useWebGL = (el: HTMLCanvasElement | null) => {
  const gl = el?.getContext('webgl');

  const attributes = useMemo(() => {
    if (gl != null) {
      return initialize(gl);
    }
    return null;
  }, [gl]);

  return {
    attributes,
    draw,
    addTranslation,
    addGain,
    drawBackground,
    gl,
  };
};

const addTranslation = (
  gl: WebGLRenderingContext,
  translationUniformLocation: WebGLUniformLocation | null,
  translation: {
    x: number;
    y: number;
  },
  length: number,
) => {
  gl.uniform2f(translationUniformLocation, translation.x, translation.y);

  const primitiveType = gl.LINES;
  const count = length / 4;
  const offset = 0;

  gl.drawArrays(primitiveType, offset, count);
};

const addGain = (
  gl: WebGLRenderingContext,
  gainUniformLocation: WebGLUniformLocation | null,
  gain = 1,
  length: number,
) => {
  gl.uniform1f(gainUniformLocation, gain);

  const primitiveType = gl.LINES;
  const count = length / 4;
  const offset = 0;

  gl.drawArrays(primitiveType, offset, count);
};

const draw = (
  points: Float32Array,
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  positionAttrLocation: number,
  initialPaddingAttrLocation: number,
  domainMinUniformLocation: WebGLUniformLocation | null,
  domainMaxUniformLocation: WebGLUniformLocation | null,
  rangeMaxUniformLocation: WebGLUniformLocation | null,
  rangeMinUniformLocation: WebGLUniformLocation | null,
  containerUniformLocation: WebGLUniformLocation | null,
  translationUniformLocation: WebGLUniformLocation | null,
  gainUniformLocation: WebGLUniformLocation | null,
  {
    range: {
      min: { x: rangeMinX = 0, y: rangeMinY = 0 },
      max: { x: rangeMaxX, y: rangeMaxY },
    },
  }: Record<'range', VectorScale>,
  {
    domain: {
      min: { x: domainMinX = 0, y: domainMinY = 0 },
      max: { x: domainMaxX, y: domainMaxY },
    },
  }: Record<'domain', VectorScale>,
  gain: number,
  translation: {
    x: number;
    y: number;
  },
) => {
  gl.bufferData(gl.ARRAY_BUFFER, points, gl.STATIC_DRAW);
  gl.useProgram(shaderProgram);
  gl.enableVertexAttribArray(positionAttrLocation);
  gl.enableVertexAttribArray(initialPaddingAttrLocation);

  // the following the components of the y and x values
  const size = 2;
  const type = gl.FLOAT;
  const normalize = false;
  const stride = 4 * Float32Array.BYTES_PER_ELEMENT;
  const offset = 0;

  gl.vertexAttribPointer(
    positionAttrLocation,
    size,
    type,
    normalize,
    stride,
    offset,
  );

  // the following the components of the y and x initial_padding
  gl.vertexAttribPointer(
    initialPaddingAttrLocation,
    size,
    type,
    normalize,
    stride,
    2 * Float32Array.BYTES_PER_ELEMENT,
  );

  gl.uniform2f(containerUniformLocation, gl.canvas.width, gl.canvas.height);
  gl.uniform2f(domainMinUniformLocation, domainMinX, domainMinY);
  gl.uniform2f(domainMaxUniformLocation, domainMaxX, domainMaxY);
  gl.uniform2f(rangeMaxUniformLocation, rangeMaxX, rangeMaxY);
  gl.uniform2f(rangeMinUniformLocation, rangeMinX, rangeMinY);
  gl.uniform2f(translationUniformLocation, translation.x, translation.y);
  gl.uniform1f(gainUniformLocation, gain);

  const primitiveType = gl.LINES;
  const count = points.length / 4;

  gl.drawArrays(primitiveType, offset, count);
};

const drawBackground = (
  gl: WebGLRenderingContext,
  canvasElement: HTMLCanvasElement,
  dimensions: Record<number, Dimension>,
  width: number,
  height: number,
  translation: {
    x: number;
    y: number;
  },
) => {
  const { left, top } = canvasElement.getBoundingClientRect();
  for (const key in dimensions) {
    const dimension = dimensions[key];
    if (
      dimension.left + width + translation.x < 0 ||
      dimension.left + translation.x > gl.canvas.width ||
      dimension.top - top + height + translation.y < 0 ||
      dimension.top - top + translation.y > gl.canvas.height
    ) {
      continue; // it's off screen
    }

    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.scissor(
      dimension.left - left + translation.x,
      gl.canvas.height - dimension.top + top - height - translation.y,
      dimension.beatLeft - dimension.left + 1, // Add 1 pixel because with the scrolling
      // sometimes the adjacent blue background get moved slightly to the right
      height,
    );
    gl.clearColor(1, 1, 1, 1);

    gl.clear(gl.COLOR_BUFFER_BIT);
    /**
     *
     * The scissor box's pixel application is reversed on the y axis compared to the screen
     * starting 0 is the lower corner of the canvas. Also on the scissor() function the 2nd
     * parameter is the bottom of the box you want to draw.
     * https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/scissor
     * So everything is reversed in the y axis (same for the y scrolling)
     *
     *
     * Example (all pixel values are concerning the y axis):
     * gl.canvas.height = 600px
     * top= 100px
     * ---------------------------------------- scissor's box max height = 600px
     *
     *
     * dimension.top = 200px
     *  ------------------------- scissorPoint: 200px + height (2nd parameter of `scissor()`)
     * |                         |
     * |                         |
     * |         CLUSTER         |
     * |                         |
     * |                         |
     *  ------------------------- scissorPoint: 200px
     * dimension:
     * top + gl.canvas.height = 700px
     * ----------------------------------------- scissor's box minimum = 0px
     *
     * gl.scissor(
     *  leftPixel,
     *  scissorPoint --> (top + gl.canvas.height) - (dimension.top + height)
     *  clusterWidth
     *  clusterHeight
     * )
     */

    gl.scissor(
      dimension.beatLeft - left + translation.x,
      gl.canvas.height - dimension.top + top - height - translation.y,
      dimension.beatRight - dimension.beatLeft + 1, // Add 1 pixel because with the scrolling
      // sometimes the adjacent white background get moved slightly to the right
      height,
    );
    gl.clearColor(0.7843137, 0.917647, 0.894117, 1);

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.scissor(
      dimension.beatRight - left + translation.x,
      gl.canvas.height - dimension.top + top - height - translation.y,
      width - dimension.beatRight + dimension.left,
      height,
    );
    gl.clearColor(1, 1, 1, 1);
    gl.clear(gl.DEPTH_BUFFER_BIT);
    // This solution depends on the scrolling but the browser
    // has a native debounce on actions like that for performance reasons
    // so sometimes when you scroll fast you see a small delay on the above scissor boxes.

    // TODO: A better solution would be to search if you can call drawArrays() twice
    // one which is the current for all the clusters and one for the boxes (the
    // boxes would be made in the form of triangles)
  }

  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
  gl.scissor(0, 0, gl.canvas.width, gl.canvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.DEPTH_BUFFER_BIT);
};

const initialize = (gl: WebGLRenderingContext) => {
  if (gl == null) {
    return;
  }

  const shaderProgram = initShaderProgram(gl, vsSource, fsSource);

  if (shaderProgram == null) {
    return;
  }

  const positionAttrLocation = gl.getAttribLocation(
    shaderProgram,
    'a_position',
  );
  const initialPaddingAttrLocation = gl.getAttribLocation(
    shaderProgram,
    'a_initial_padding',
  );
  const rangeMinUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_range_min',
  );
  const rangeMaxUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_range_max',
  );
  const domainMaxUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_domain_max',
  );
  const domainMinUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_domain_min',
  );
  const containerUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_container',
  );
  const gainUniformLocation = gl.getUniformLocation(shaderProgram, 'u_gain');
  const translationUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_translation',
  );

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.enable(gl.SCISSOR_TEST);

  return {
    positionAttrLocation,
    initialPaddingAttrLocation,
    rangeMinUniformLocation,
    rangeMaxUniformLocation,
    domainMaxUniformLocation,
    domainMinUniformLocation,
    containerUniformLocation,
    translationUniformLocation,
    gainUniformLocation,
    shaderProgram,
    positionBuffer,
  };
};

const vsSource = `
  attribute vec2 a_position;
  attribute vec2 a_initial_padding;
  uniform vec2 u_translation;
  uniform vec2 u_range_min;
  uniform vec2 u_range_max;
  uniform float u_gain;
  uniform vec2 u_domain_max;
  uniform vec2 u_domain_min;
  uniform vec2 u_container;

  void main() {
    // divide y min and max values by u_gain to apply the amplitude
    vec2 domainWithGainMin = vec2(
      u_domain_min.x,
      u_domain_min.y / u_gain
    );

    vec2 domainWithGainMax = vec2(
      u_domain_max.x,
      u_domain_max.y / u_gain
    );

    // we transform the positions to pixels
    // formula: pointToDomainPixel = range_min + (range_max - range_min) * (position - domain_min)/ (domain_max - domain_min)
    vec2 pointsToDomainPixels = vec2(
      u_range_min.x + (u_range_max.x - u_range_min.x) * (a_position.x - domainWithGainMin.x)/ (domainWithGainMax.x - domainWithGainMin.x),
      u_range_min.y + (u_range_max.y - u_range_min.y) * (a_position.y - domainWithGainMin.y)/ (domainWithGainMax.y - domainWithGainMin.y)
    );

    // Round values to their min or max if they are out of
    // their cluster container limits u_range_max.y, u_range_min.y
    // this can happen when we change the amplitude
    vec2 sanitizedValues = vec2(pointsToDomainPixels);
    if (pointsToDomainPixels.y > u_range_max.y) {
      sanitizedValues = vec2(
        pointsToDomainPixels.x,
        u_range_max.y
      );
    } else if (pointsToDomainPixels.y < u_range_min.y) {
      sanitizedValues = vec2(
        pointsToDomainPixels.x,
        u_range_min.y
      );
    }

    // all the clusters are now positioned in the range [u_range_min, u_range_max]
    // but we want to add their initial padding so we can position them correctly
    // inside their BaseCluster/ Morphology containers.

    // We add the initial padding to each attribute and then divide them by
    // the dimensions of the canvas container to move them to the range [0, 1].

    // Finally we add the translation which happens when we scroll
    vec2 zeroToOne = vec2(
      ((sanitizedValues.x + a_initial_padding.x) / u_container.x) + u_translation.x,
      ((sanitizedValues.y + a_initial_padding.y) / u_container.y) + u_translation.y
    );

    // the values now are in the range [0, 1]
    // we want to transform in the range [0, 2]
    vec2 zeroToTwo = zeroToOne * 2.0;

    // we move the values to the acceptable (by WebGL) range [-1, 1]
    vec2 clipSpace = vec2(zeroToTwo - 1.0);
    gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
  }
`;

const fsSource = `
  precision mediump float;

  void main() {
    vec4 color = vec4(0.0, 0.0, 0.0, 1.0);
    gl_FragColor = color;
  }
`;

const initShaderProgram = (
  gl: WebGLRenderingContext,
  vsSource: string,
  fsSource: string,
) => {
  const vertexShader = loadShader(gl, gl.VERTEX_SHADER, vsSource);
  const fragmentShader = loadShader(gl, gl.FRAGMENT_SHADER, fsSource);

  const shaderProgram = gl.createProgram();
  if (shaderProgram == null || vertexShader == null || fragmentShader == null) {
    return;
  }
  gl.attachShader(shaderProgram, vertexShader);
  gl.attachShader(shaderProgram, fragmentShader);
  gl.linkProgram(shaderProgram);

  if (gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
    return shaderProgram;
  }
  gl.deleteProgram(shaderProgram);
};

const loadShader = (
  gl: WebGLRenderingContext,
  type: number,
  source: string,
) => {
  const shader = gl.createShader(type);

  if (shader == null) {
    return;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    return shader;
  }
  gl.deleteShader(shader);
};

export const clearCanvas = (gl: WebGLRenderingContext) => {
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
};

/**
 *
 * @param clusters
 * @param dimensions will give the top and left pixel screen
 * attributes so we know what padding to give on the
 * points of each cluster
 * @param canvasElement the canvas element from which we will take
 * the screen positions
 * @param scroll the scrolling of the user
 * @returns an array to be used by the webGL.
 * In order for webGL to draw lines it needs 4 sets of points,
 * see here https://webglfundamentals.org/webgl/lessons/webgl-points-lines-triangles.html
 */
export const getLinePositions = (
  clusters: Cluster[],
  dimensions: Record<number, Dimension>,
  canvasElement: HTMLCanvasElement,
) => {
  const arr: number[] = [];

  const canvas = canvasElement.getBoundingClientRect();
  clusters.forEach((cluster) => {
    const el = dimensions[cluster.id];
    if (el == null) {
      return;
    }
    cluster.beats.forEach((beat) => {
      beat.data.forEach((datum, i) => {
        const currentIndex = arr.length;
        const nextPoint = beat.data[i + 1];
        // Remove from the initial padding the padding of the canvas element
        // so that the repositioning will be correct
        // after every split or un-merge.
        const xInitialPadding = el.left - canvas.left;
        const yInitialPadding = el.top - canvas.top;

        // The initial padding is in pixels while the
        // x and y values are in their original scale
        if (datum != null && nextPoint != null) {
          // first 2 components of the 1st vertex with the x1, y1 values of
          // the line
          arr[currentIndex] = i;
          arr[currentIndex + 1] = datum;

          // next 2 components of the initial padding that we will add to
          // x1 and y1 respectively
          arr[currentIndex + 2] = xInitialPadding;
          arr[currentIndex + 3] = yInitialPadding;

          // this is the 2nd vertex of the line with the x2, y2 and
          // their respective initial paddings
          arr[currentIndex + 4] = i + 1;
          arr[currentIndex + 5] = nextPoint;
          arr[currentIndex + 6] = xInitialPadding;
          arr[currentIndex + 7] = yInitialPadding;
        }
      });
    });
  });

  return new Float32Array(arr);
};
